import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  InputBase,
  alpha,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Visibility as PreviewIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as CompilingIcon,
} from '@mui/icons-material';
import { DWLFFSMBuilder, FSMBuilderAPI, eventTypesFromCatalogue } from '@andywilliams/dwlf-charting';
import { mergeEventTypes } from './fsmEventTypes';
import SuccessSnackbar from '../../components/SuccessSnackbar';
import { customEventsApi } from '../../api/customEvents';
import { fsmApi } from '../../api/fsm';
import useNodeTypes from '../../hooks/useNodeTypes';
import {
  fetchFSM,
  createFSM,
  updateFSM,
  clearCurrentFSM,
  clearSuccessMessage,
  clearErrors,
} from './fsmSlice';
import DesktopBestNotice from '../../components/DesktopBestNotice';

const FSMBuilderPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { fsmId } = useParams();
  const isEditing = Boolean(fsmId);
  const isViewOnly = location.pathname.endsWith('/view');

  const {
    currentFSM,
    currentFSMLoading,
    currentFSMError,
    isCreating,
    isUpdating,
    successMessage,
  } = useSelector((state) => state.fsm);

  // Local state
  // The event types a state machine can listen for are the node catalogue's
  // event nodes (DWLF-370), not a list kept here.
  const { catalogue, failed: catalogueFailed } = useNodeTypes();
  const [fsmName, setFsmName] = useState('');
  const [fsmDescription, setFsmDescription] = useState('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [customEventDefinitions, setCustomEventDefinitions] = useState([]);
  const [compileStatus, setCompileStatus] = useState(null); // null | 'compiling' | 'success' | 'error'
  const [validationWarningOpen, setValidationWarningOpen] = useState(false);
  const [validationWarnings, setValidationWarnings] = useState([]);
  

  // Ref for the FSM builder
  const fsmBuilderRef = useRef(null);
  const graphLoadTimeoutRef = useRef(null);
  const compileTimeoutRef = useRef(null);
  const hasUnsavedChangesRef = useRef(false);
  const skipNextGraphImportRef = useRef(false);
  const fetchRequestIdRef = useRef(0);
  const GRAPH_RELOAD_DELAY_MS = 1000;

  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  useEffect(() => {
    return () => {
      if (graphLoadTimeoutRef.current) {
        clearTimeout(graphLoadTimeoutRef.current);
      }
      if (compileTimeoutRef.current) {
        clearTimeout(compileTimeoutRef.current);
      }
    };
  }, []);

  // Load FSM for editing
  useEffect(() => {
    const requestId = ++fetchRequestIdRef.current;
    if (isEditing && fsmId) {
      const promise = dispatch(fetchFSM(fsmId));
      // If a newer request was made (navigation), abort the stale one
      promise.then(() => {
        if (fetchRequestIdRef.current !== requestId) {
          // Stale response — clear it so it doesn't overwrite fresh state
          dispatch(clearCurrentFSM());
        }
      });
    }
    
    return () => {
      fetchRequestIdRef.current++;  // invalidate any in-flight request
      dispatch(clearCurrentFSM());
      dispatch(clearErrors());
    };
  }, [dispatch, isEditing, fsmId]);

  // Load custom events for eventTypes prop
  useEffect(() => {
    let isActive = true;

    const loadCustomEvents = async () => {
      try {
        const response = await customEventsApi.listEvents({ limit: 200 });
        const items = Array.isArray(response?.events)
          ? response.events
          : response?.customEvents || [];
        if (isActive) {
          setCustomEventDefinitions(items);
        }
      } catch (error) {
        console.warn('Failed to load custom events for FSM builder', error);
        if (isActive) {
          setCustomEventDefinitions([]);
        }
      }
    };

    loadCustomEvents();
    return () => {
      isActive = false;
    };
  }, []);

  // Set form data when FSM loads (or clear when navigating to new)
  useEffect(() => {
    if (graphLoadTimeoutRef.current) {
      clearTimeout(graphLoadTimeoutRef.current);
    }

    if (currentFSM) {
      setFsmName(currentFSM.name || '');
      setFsmDescription(currentFSM.description || '');

      graphLoadTimeoutRef.current = setTimeout(() => {
        if (skipNextGraphImportRef.current) {
          skipNextGraphImportRef.current = false;
          return;
        }
        if (hasUnsavedChangesRef.current) {
          return;
        }
        if (fsmBuilderRef.current && currentFSM.visual) {
          fsmBuilderRef.current.setVisual(currentFSM.visual);
        }
      }, GRAPH_RELOAD_DELAY_MS);
    } else {
      // Clear form fields when navigating to create page
      setFsmName('');
      setFsmDescription('');
    }
  }, [currentFSM]);

  // Handle success messages
  useEffect(() => {
    if (successMessage) {
      setSnackbarOpen(true);
      
      if (successMessage.includes('saved') || successMessage.includes('created') || successMessage.includes('updated')) {
        setHasUnsavedChanges(false);
        
        setTimeout(() => {
          dispatch(clearSuccessMessage());
          setSnackbarOpen(false);
        }, 3000);
      } else {
        setTimeout(() => {
          dispatch(clearSuccessMessage());
          setSnackbarOpen(false);
        }, 4000);
      }
    }
  }, [successMessage, dispatch]);

  const handleBack = () => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (!confirmLeave) {
        return;
      }
    }
    navigate('/state-machines');
  };

  const handleSave = () => {
    if (!fsmName.trim()) {
      alert('Please enter a state machine name');
      return;
    }
    setSaveDialogOpen(true);
  };

  const handleSaveConfirm = async () => {
    if (!fsmBuilderRef.current) {
      alert('FSM builder not ready');
      return;
    }

    try {
      const visual = fsmBuilderRef.current.getVisual();

      // Validate with FSMBuilderAPI
      const validation = FSMBuilderAPI.validateFSM(visual);
      
      // Check for validation errors first - block save
      if (validation && validation.errors && validation.errors.length > 0) {
        const errorMessages = (validation.errors || []).map(e => e.message || e).join('\n');
        alert(errorMessages || 'FSM validation failed. Please check your state machine.');
        return;
      }

      // Block if FSM is invalid (valid===false) - must fix errors first
      if (validation && validation.valid === false) {
        alert('Cannot save invalid FSM. Please fix all errors first.');
        return;
      }

      // Show warning dialog only when FSM is valid but has warnings
      if (validation && validation.warnings && validation.warnings.length > 0) {
        setValidationWarnings(validation.warnings?.map(w => w.message || w) || []);
        setSaveDialogOpen(false);
        setValidationWarningOpen(true);
        return;
      }

      await performSave(visual);
    } catch (error) {
      skipNextGraphImportRef.current = false;
      console.error('Error saving state machine:', error);
      alert('Error saving state machine. Please try again.');
    }
  };

  // Helper function to handle FSM compile result - reduces duplication
  // Returns the promise so callers can await if needed
  const handleCompileResult = (compilePromise) => {
    // Clear any existing timeout to prevent stale timeouts from resetting status mid-compile
    if (compileTimeoutRef.current) {
      clearTimeout(compileTimeoutRef.current);
    }

    setCompileStatus('compiling');
    return compilePromise
      .then(() => {
        setCompileStatus('success');
        compileTimeoutRef.current = setTimeout(() => setCompileStatus(null), 3000);
      })
      .catch((compileErr) => {
        // Only ignore 404 (endpoint not deployed yet) — rethrow other errors
        if (compileErr?.response?.status === 404 || compileErr?.status === 404) {
          console.warn('FSM compile endpoint not available, will compile at runtime');
          setCompileStatus(null);
        } else {
          console.error('FSM compile failed:', compileErr);
          setCompileStatus('error');
          compileTimeoutRef.current = setTimeout(() => setCompileStatus(null), 5000);
        }
      });
  };

  const performSave = async (visual) => {
    skipNextGraphImportRef.current = true;
    const fsmData = {
      name: fsmName.trim(),
      description: fsmDescription.trim(),
      visual,
    };

    let savedFsmId = fsmId;
    if (isEditing) {
      await dispatch(updateFSM({ 
        fsmId, 
        updates: fsmData,
      })).unwrap();
    } else {
      const result = await dispatch(createFSM({ ...fsmData, status: 'draft' })).unwrap();
      if (result?.fsmId) {
        savedFsmId = result.fsmId;
        
        // Compile first (await), then navigate - so status updates are visible before unmount
        if (fsmApi?.compileFSM) {
          await handleCompileResult(fsmApi.compileFSM(savedFsmId));
        }
        navigate(`/state-machines/${result.fsmId}/edit`, { replace: true });
      }
    }

    setSaveDialogOpen(false);
    setValidationWarningOpen(false);
    
    // Auto-compile after save for existing FSMs (not new - that's handled above)
    if (isEditing && savedFsmId && fsmApi?.compileFSM) {
      handleCompileResult(fsmApi.compileFSM(savedFsmId));
    }
  };

  const handleWarningDialogClose = () => {
    setValidationWarningOpen(false);
  };

  const handleWarningDialogProceed = async () => {
    // Don't close dialog here — performSave closes it on success.
    // Closing early means a failed save dismisses the dialog and forces the user to restart the whole flow.
    if (!fsmBuilderRef.current) {
      alert('FSM builder not ready');
      return;
    }

    try {
      const visual = fsmBuilderRef.current.getVisual();

      // Re-validate - invalid FSMs cannot be saved even with "Save Anyway"
      const validation = FSMBuilderAPI.validateFSM(visual);
      if (validation && validation.valid === false) {
        alert('Cannot save invalid FSM. Please fix all errors first.');
        return;
      }

      await performSave(visual);
    } catch (err) {
      skipNextGraphImportRef.current = false;
      alert('Save failed: ' + (err.message || 'Unknown error'));
    }
  };

  const handleSaveCancel = () => {
    setSaveDialogOpen(false);
  };

  const handleFSMChange = () => {
    setHasUnsavedChanges(true);
  };

  const handlePreview = () => {
    if (fsmBuilderRef.current) {
      const visual = fsmBuilderRef.current.getVisual();
      console.log('FSM Preview:', JSON.stringify(visual, null, 2));
    }
  };

  // Derive eventTypes: the catalogue's indicator events first, then user's custom events
  const eventTypes = useMemo(() => {
    return mergeEventTypes(eventTypesFromCatalogue(catalogue), customEventDefinitions);
  }, [catalogue, customEventDefinitions]);

  if (currentFSMLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h6">Loading state machine...</Typography>
      </Container>
    );
  }

  if (currentFSMError) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">
          {currentFSMError}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      height: 'calc(100vh - 64px)',
      display: 'flex', 
      flexDirection: 'column', 
      overflow: 'hidden'
    }}>
      {/* Top App Bar */}
      <AppBar position="static" color="default" elevation={1} sx={{ flexShrink: 0 }}>
        <Toolbar>
          <IconButton
            edge="start"
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            <BackIcon />
          </IconButton>
          
          {/* FSM Name Input */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary', minWidth: 'auto' }}>
              {isViewOnly ? 'View:' : isEditing ? 'Edit:' : 'New:'}{hasUnsavedChanges && ' *'}
            </Typography>
            <InputBase
              placeholder="Enter state machine name..."
              value={fsmName}
              onChange={(e) => {
                setFsmName(e.target.value);
                setHasUnsavedChanges(true);
              }}
              readOnly={isViewOnly}
              sx={{
                flex: 1,
                maxWidth: '400px',
                fontSize: '1.1rem',
                fontWeight: 500,
                color: 'text.primary',
                backgroundColor: alpha('#000', 0.04),
                borderRadius: 1,
                padding: '8px 12px',
                border: '1px solid transparent',
                '&:hover': {
                  backgroundColor: isViewOnly ? alpha('#000', 0.04) : alpha('#000', 0.08),
                },
                '&:focus': {
                  backgroundColor: 'background.paper',
                  borderColor: 'primary.main',
                  outline: 'none',
                },
                '&::placeholder': {
                  color: 'text.disabled',
                  opacity: 1,
                },
              }}
            />
          </Box>
          
          <Button
            startIcon={<PreviewIcon />}
            onClick={handlePreview}
            sx={{ mr: 2 }}
          >
            Preview
          </Button>
          
          {!isViewOnly && (
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={isCreating || isUpdating || !fsmName.trim()}
              sx={{ 
                backgroundColor: hasUnsavedChanges ? '#ff6b35' : 'var(--dwlf-accent)',
                color: 'var(--dwlf-bg)',
                '&:hover': {
                  backgroundColor: hasUnsavedChanges ? '#e55a2b' : 'var(--dwlf-text)',
                }
              }}
            >
              {isCreating || isUpdating ? 'Saving...' : hasUnsavedChanges ? 'Save Changes' : 'Save'}
            </Button>
          )}
          
          {/* Compile Status */}
          {compileStatus && (
            <Chip
              icon={
                compileStatus === 'compiling' ? <CompilingIcon /> :
                compileStatus === 'success' ? <SuccessIcon /> :
                <ErrorIcon />
              }
              label={
                compileStatus === 'compiling' ? 'Compiling...' :
                compileStatus === 'success' ? 'Compiled' :
                'Compile Failed'
              }
              color={
                compileStatus === 'compiling' ? 'default' :
                compileStatus === 'success' ? 'success' :
                'error'
              }
              sx={{ ml: 2 }}
              size="small"
            />
          )}
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <DesktopBestNotice what="The state-machine builder" />
      {catalogueFailed && (
        <Alert severity="warning" sx={{ mx: 2, mb: 1 }}>
          The event catalogue could not be loaded, so indicator events cannot be listed. Reload to retry.
        </Alert>
      )}
      <Box sx={{ 
        flex: 1, 
        overflow: 'hidden',
        position: 'relative',
        minHeight: 0,
      }}>
        <DWLFFSMBuilder
          ref={fsmBuilderRef}
          onChange={handleFSMChange}
          eventTypes={eventTypes}
          readOnly={isViewOnly}
          style={{ 
            width: '100%',
            height: '100%',
          }}
        />
      </Box>

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onClose={handleSaveCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditing ? `Update "${fsmName}"` : `Save "${fsmName}"`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Description (Optional)"
              value={fsmDescription}
              onChange={(e) => setFsmDescription(e.target.value)}
              multiline
              rows={4}
              placeholder="Describe your state machine, its states, and transitions..."
              helperText="A good description helps you understand this state machine later"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSaveCancel}>Cancel</Button>
          <Button 
            onClick={handleSaveConfirm} 
            variant="contained"
            disabled={isCreating || isUpdating}
          >
            {isCreating || isUpdating ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validation Warning Dialog */}
      <Dialog open={validationWarningOpen} onClose={handleWarningDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#ff9800' }}>
          ⚠️ Validation Warnings
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            The following warnings were found:
          </Alert>
          <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
            {validationWarnings.map((warning, index) => (
              <Typography key={index} variant="body2" sx={{ mb: 1, pl: 2 }}>
                • {warning}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWarningDialogClose}>Cancel</Button>
          <Button 
            onClick={handleWarningDialogProceed} 
            variant="contained"
            color="warning"
            disabled={isCreating || isUpdating}
          >
            Save Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <SuccessSnackbar
        open={snackbarOpen}
        message={successMessage}
        onClose={() => setSnackbarOpen(false)}
        autoHideDuration={3000}
      />
    </Box>
  );
};

export default FSMBuilderPage;
